import type { CampaignState, WaveRecord } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { anyDeeds, deedsBoard, waveLines } from './deeds.ts';
import { h } from '../ui/dom.ts';
import { fmtDuration } from '../ui/format.ts';
import { ICONS } from '../ui/icons.ts';

/** The compass point a cell lies toward from the keep (north is up the board at its first turn). */
const compass = (q: number, r: number): string => {
  const x = q + r / 2;
  const y = r * 0.866;
  const a = Math.atan2(-y, x);
  return ['e', 'ne', 'n', 'nw', 'w', 'sw', 's', 'se'][((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8] as string;
};

/**
 * One wave at a glance: result, how the keep ended, who defended and how it went. Used by the report after a wave and by the wave history.
 */
export const waveCard = (
  s: CampaignState,
  w: WaveRecord,
  opts: { now?: number; me?: string; onReplay?: (sector: number) => void } = {},
): HTMLElement => {
  const won = w.result === 'won';
  const player = (pid: string): { name: string; color: string } | null => {
    const p = s.players.find((x) => x.id === pid);
    return p ? { name: p.displayName, color: p.color } : null;
  };

  const head = h(
    'div.wave-head',
    {},
    h('h4', {}, t('history.wave', { m: w.marea + 1, w: w.wave + 1 })),
    h(`span.badge.${won ? 'won' : 'lost'}`, {}, t(won ? 'history.won' : 'history.lost')),
    opts.now !== undefined ? h('span.small', {}, t('log.ago', { t: fmtDuration(opts.now - w.at) })) : '',
  );

  const pct = w.keepMaxHp > 0 ? Math.max(0, Math.min(100, Math.round((100 * w.keepHp) / w.keepMaxHp))) : 0;
  const keep = h(
    'div.wave-keep',
    {},
    h('span.small', {}, t('bastion.keep')),
    h(`div.bar${pct > 40 ? '' : '.low'}`, {}, h('i', { style: `width:${pct}%` })),
    h('span.small.num', {}, `${w.keepHp} / ${w.keepMaxHp}`),
  );

  // The wave is one battle: who fought it (live or as ghosts), how the gates held, how it went.
  const ids: string[] = [];
  for (const sec of w.sectors) for (const pid of sec.defenders) if (!ids.includes(pid)) ids.push(pid);
  const ghost = w.sectors.every((sec) => sec.source === 'offline');
  const kills = w.sectors.reduce((n, sec) => n + sec.kills, 0);
  const leaks = w.sectors.reduce((n, sec) => n + sec.leaks, 0);
  const gatesFell = w.sectors.filter((sec) => sec.gateFell).length;
  const who = h('div.ws-who');
  // Who was there live and who fought as a ghost (older waves only know it per battle).
  const live = w.live ? new Set(w.live) : null;
  for (const pid of ids) {
    const p = player(pid);
    if (!p) continue;
    if (!live) {
      who.append(h('span.who-chip', { style: `--who:${p.color}` }, p.name));
      continue;
    }
    const ghostly = !live.has(pid);
    who.append(h(`span.who-chip${ghostly ? '.as-ghost' : ''}`, { style: `--who:${p.color}`, title: t(ghostly ? 'bastion.byGhost' : 'bastion.byLive') }, h('span', { html: ghostly ? ICONS.ghost : ICONS.sword }), p.name));
  }
  if (!who.childElementCount) who.append(h('span.small', {}, '—'));
  // Older waves only know whether anyone was there live.
  if (!live) who.append(h(`span.how${ghost ? '.ghost' : ''}`, {}, h('span', { html: ghost ? ICONS.ghost : ICONS.sword }), t(ghost ? 'history.asGhosts' : 'history.mixed')));
  const fight = h(
    `section.wave-sector${gatesFell || leaks > 0 ? '.hurt' : ''}`,
    {},
    h(
      'div.ws-head',
      {},
      h('b', {}, t('history.defenders')),
      h(`span.ws-gate${gatesFell ? '.bad' : '.good'}`, {}, h('span', { html: ICONS.shield }), gatesFell ? t(gatesFell === 1 ? 'history.gateFellOne' : 'history.gatesFell', { n: gatesFell }) : t('history.gatesHeld')),
      opts.onReplay ? h('button.iconbtn.small', { 'data-test': 'replay', 'aria-label': t('bastion.watch'), title: t('bastion.watch'), html: ICONS.play, onclick: () => opts.onReplay?.(0) }) : '',
    ),
    who,
    h(
      'div.ws-stats',
      {},
      h('span.ws-stat', {}, h('span', { html: ICONS.skull }), h('b.num', {}, String(kills)), t('history.killsLbl')),
      h(`span.ws-stat${leaks > 0 ? '.bad' : '.good'}`, {}, h('span', { html: ICONS.keep }), h('b.num', {}, String(leaks)), t('history.leaksLbl')),
    ),
  );

  // Notable moments as chips (a fallen gate is already in the table).
  const chips = h('div.wave-chips');
  const seen = new Set<string>();
  for (const hl of w.highlights) {
    const [kind, pid, , what] = hl.split(':');
    if (kind === 'gateFell') continue;
    const who = (pid && player(pid)?.name) || t('moment.someone');
    let text = '';
    if (kind === 'eliteSlain') text = t('moment.eliteSlain', { who });
    else if (kind === 'bossSlain' && what !== w.unsleeping?.id) text = t('moment.bossSlain', { who, what: t(`enemy.${what}`) });
    else if (kind === 'oath' && pid) text = t('moment.oath', { who });
    else if (kind === 'bossPhase') text = t('moment.bossPhase');
    if (text && !seen.has(text)) {
      seen.add(text);
      chips.append(h('span.chip-moment', {}, text));
    }
  }
  // One of the Unsleeping came (docs/ANTIHEROES.md): how it ended.
  const un = w.unsleeping;
  if (un) {
    const by = (un.pid && player(un.pid)?.name) || t('moment.someone');
    chips.append(h(`span.chip-moment.unsleeping.${un.outcome}`, { 'data-test': 'wave-unsleeping' }, t(`unsleeping.${un.id}.${un.outcome}`, { who: by })));
  }
  // Terraced land: where the fissures cracked open, by the compass (said only once the wave is over).
  if (w.rifts?.length) {
    const sides = [...new Set(w.rifts.map((r) => compass(r.q, r.r)))];
    chips.append(h('span.chip-moment.rifts', { 'data-test': 'wave-rifts' }, t('history.rifts', { list: sides.map((k) => t(`compass.${k}`)).join(', ') })));
  }
  // The experience my hero took from it (live or as a ghost).
  const xp = opts.me ? (w.xp?.[opts.me] ?? 0) : 0;
  if (xp > 0) chips.append(h('span.chip-moment.xp', { 'data-test': 'wave-xp' }, h('span', { html: ICONS.hero }), t(w.live && !w.live.includes(opts.me as string) ? 'history.xpGhost' : 'history.xp', { n: xp })));
  if (w.dust > 0) chips.append(h('span.chip-moment.dust', {}, h('span', { html: ICONS.dust }), `+${w.dust}`));
  // What the quarries and the grove put in the storehouse.
  const q = w.yields?.quarry;
  if (q) chips.append(h('span.chip-moment', { 'data-test': 'wave-quarry' }, h('span', { html: ICONS.stone }), t('history.quarry', { wood: q.wood, stone: q.stone })));
  if (w.yields?.grove) chips.append(h('span.chip-moment', { 'data-test': 'wave-grove' }, h('span', { html: ICONS.wood }), t('history.grove', { n: w.yields.grove })));
  // What the sanctum mended (era IV).
  if (w.yields?.sanctum) chips.append(h('span.chip-moment', { 'data-test': 'wave-sanctum' }, t('history.sanctum', { n: w.yields.sanctum })));

  // A wave after the keep already fell in this Tide isn't fought: say so instead of an empty table.
  const content = w.sectors.length ? fight : h('p.hint', {}, t('history.skipped'));
  // Its deeds, folded: built only when opened (the history can hold sixty waves).
  let deeds: HTMLElement | '' = '';
  const d = w.deeds;
  if (d && anyDeeds(waveLines(s, d, w.live))) {
    const fold = h('details.wave-deeds', { 'data-test': 'wave-deeds' }, h('summary', {}, h('span', { html: ICONS.sword }), t('deeds.waveOpen')));
    fold.addEventListener('toggle', () => {
      if (fold.open && fold.childElementCount === 1) fold.append(deedsBoard(waveLines(s, d, w.live, opts.me), { titles: true }));
    });
    deeds = fold;
  }
  return h(`article.wave-card.${won ? 'won' : 'lost'}`, {}, head, keep, content, deeds, chips.childElementCount ? chips : '');
};

/** Every fought wave, newest first. */
export const historyPanel = (s: CampaignState, now: number, onReplay?: (marea: number, wave: number, sector: number) => void, me?: string): HTMLElement => {
  const wrap = h('div.history');
  const waves = s.waves.filter((w) => w.sectors.length > 0).slice().reverse();
  if (!waves.length) wrap.append(h('p.hint', {}, t('history.empty')));
  for (const w of waves) wrap.append(waveCard(s, w, { now, ...(me ? { me } : {}), ...(onReplay ? { onReplay: (sec: number) => onReplay(w.slot ?? w.marea, w.wave, sec) } : {}) }));
  return wrap;
};
